import type { ApolloError } from '@apollo/client';
import * as _ from 'lodash-es';
import { useEffect } from 'react';
import handleApolloError from './handleApolloError';
import type { Dispatch } from '../../app/redux/useAppSelector';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type Toast from '../../components/Toast';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../router/useNavigate';

type ToastProps = Partial<
  Pick<
    React.ComponentProps<typeof Toast>,
    | 'message'
    | 'primaryText'
    | 'onPrimaryClick'
    | 'secondaryText'
    | 'onSecondaryClick'
  >
>;

interface ShowErrorAndRedirectOptions {
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
  message: string;
  title?: string;
  toastProps?: ToastProps;
}

const redirectOnError = ({
  dispatch,
  navigate,
  message,
  title,
  toastProps,
}: ShowErrorAndRedirectOptions) => {
  dispatch(
    UIActions.pushToast({
      level: 'error',
      message,
      title,
      ...toastProps,
    }),
  );
  navigate('/', { replace: true });
};

type NOUN = 'topic' | 'post' | 'group' | 'user';

interface RedirectForErrorOptions {
  noun: NOUN;
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
  message?: string;
  toastProps?: ToastProps;
}

export const redirectOnNotFoundError = ({
  noun,
  dispatch,
  navigate,
}: RedirectForErrorOptions) =>
  redirectOnError({
    dispatch,
    navigate,
    message: `${_.capitalize(noun)} not found`,
    title: 'Oops',
  });

export const redirectOnForbiddenError = ({
  noun,
  dispatch,
  navigate,
  toastProps,
}: RedirectForErrorOptions) =>
  redirectOnError({
    dispatch,
    navigate,
    message: `You do not have permission to view this ${noun}`,
    toastProps,
  });

interface UseErrorRedirectEffectOptions {
  noun: NOUN;
  error?: ApolloError;
  toastProps?: ToastProps;
}

const handleError = ({
  dispatch,
  error,
  noun,
  navigate,
  toastProps,
}: {
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
  error: ApolloError;
  toastProps?: ToastProps;
} & UseErrorRedirectEffectOptions) => {
  handleApolloError(error, {
    NOT_FOUND: () => {
      redirectOnNotFoundError({ noun, dispatch, navigate });
    },
    FORBIDDEN: () => {
      redirectOnForbiddenError({ noun, dispatch, navigate, toastProps });
    },
  });
};

const useErrorRedirectEffect = (options: UseErrorRedirectEffectOptions) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (options.error) {
      handleError({ dispatch, ...options, error: options.error, navigate });
    }
  });
};

export default useErrorRedirectEffect;
